import React, { useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { find, isEmpty, omit, snakeCase } from "lodash";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import DeleteModal from "@/components/DeleteModal";
import { graphql } from "@/gql";
import { BodyMeasurementDetailsModalFragment } from "@/gql/graphql";
import useAlert from "@/hooks/useAlert";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import { kilogramsToPounds, round, truncate } from "@/utils/number";

import AccordionSummaryItem from "./AccordionSummaryItem";
import AddBodyMeasurementModal from "./AddBodyMeasurementModal";
import BodyMeasurementDetailsModal from "./BodyMeasurementDetailsModal";
import OptionMenu from "./OptionMenu";
import { getDevineIdealBodyWeight } from "../utils/getIdealBodyWeight";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: "flex",
    backgroundColor: colors.white,
    flexDirection: "column",
    margin: theme.spacing(0, 2, 4, 2),
    height: "calc(100vh - 315px)",
  },
  header: {
    margin: theme.spacing(3, 0, 3, 0),
  },
  headerText: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    margin: theme.spacing(4),
  },
  summaryItems: {
    marginRight: theme.spacing(2),
  },
  navBar: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    margin: theme.spacing(0, 4),
  },
  tableContainer: {
    display: "flex",
    alignItems: "center",
    overflowX: "scroll",
    overflowY: "scroll",
    width: "100%",
  },
  table: {
    borderCollapse: "separate",
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },
  dateLabel: {
    minWidth: "150px",
  },
  stickyLeftCell: {
    position: "sticky",
    left: 0,
    borderRight: "4px solid #DEE6F0 !important",
  },
  moreCell: {
    borderLeft: "4px solid #DEE6F0 !important",
  },
  cellLabel: {
    fontWeight: 700,
  },
  moreLabel: {
    minWidth: "80px",
    color: "#1FD4AF !important",
  },
  moreButton: {
    color: colors.primary,
    padding: "8px",
    border: "1px solid #1FD4AF",
  },
  stickyRightCell: {
    position: "sticky",
    right: "0",
    zIndex: 1,
  },
}));

const StyledTableCell = withStyles(TableCell, () => ({
  root: {
    border: "1px solid #EDF0F5",
    backgroundColor: colors.white,
  },
}));

const StyledTableCellHead = withStyles(TableCell, () => ({
  root: {
    backgroundColor: "#EDF0F5",
    border: "1px solid #DEE6F0",
    color: colors.grey,
    textAlign: "left",
    verticalAlign: "text-top",
  },
}));

const StyledTableHeadRow = withStyles(TableRow, (theme: Theme) => ({
  root: {
    height: theme.typography.pxToRem(0.1),
  },
}));

const StyledTableRow = withStyles(TableRow, (theme: Theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const StyledButton = withStyles(Button, (theme: Theme) => ({
  root: {
    height: theme.typography.pxToRem(32),
    width: theme.typography.pxToRem(63),
    marginRight: 8,
    border: "2px solid #1FD4AF",
    fontSize: theme.typography.pxToRem(12),
    padding: theme.spacing(2),
  },
  disabled: {
    border: 0,
  },
}));

type FieldType = "defaultField" | "additionalField";

type Field = {
  [key: string]: FieldType;
};

export const FIELDS: Field = {
  weightKg: "defaultField",
  abdominalGirthCm: "defaultField",
  heightCm: "defaultField",
  ankleGirthCm: "defaultField",
  armFlexGirthCm: "defaultField",
  armRelaxedGirthCm: "defaultField",
  calfGirthCm: "defaultField",
  chestGirthCm: "defaultField",
  forearmGirthCm: "defaultField",
  headCircumferenceCm: "defaultField",
  hipCircumferenceCm: "defaultField",
  neckGirthCm: "defaultField",
  shoulderGirthCm: "defaultField",
  thighGirthCm: "defaultField",
  waistCircumferenceCm: "defaultField",
  wristGirthCm: "defaultField",
  abdominalSkinfoldMm: "additionalField",
  medicalCalfSkinfoldMm: "additionalField",
  abdominalSubcutaneousFatThicknessMm: "additionalField",
  subscapularSkinfoldMm: "additionalField",
  ankleBreadthCm: "additionalField",
  suprailiacSkinfoldMm: "additionalField",
  armPerimeterCm: "additionalField",
  supraspinalSkinfoldMm: "additionalField",
  bicepsSkinfoldMm: "additionalField",
  tricepSkinfoldMm: "additionalField",
  chestSkinfoldMm: "additionalField",
  wristBreadthCm: "additionalField",
  elbowBreadthMm: "additionalField",
  fiveSkinfoldSum: "additionalField",
  femurBreadthMm: "additionalField",
  waistToHipRatio: "additionalField",
  frontThighSkinfoldMm: "additionalField",
  eightSkinfoldSum: "additionalField",
};

export const bodyMeasurementsHistoryTableQuery = graphql(`
  query BodyMeasurementsHistoryTable($id: ID!) {
    client(id: $id) {
      id
      journalGoal {
        id
        targetWeightKg
      }
      latestHeightCm {
        id
        value
      }
      latestWeightKg {
        id
        value
      }
      profile {
        id
        gender
      }
      setting {
        ...BodyMeasurementListItem_Setting
      }
      bodyMeasurementsByDay {
        nodes {
          day
          bodyMeasurements {
            ...BodyMeasurementListItem
          }
        }
      }
    }
  }
`);

const deleteBodyMeasurementsMutation = graphql(`
  mutation DeleteBodyMeasurements($input: DeleteBodyMeasurementsInput!) {
    deleteBodyMeasurements(input: $input) {
      client {
        id
      }
    }
  }
`);

const BodyMeasurementHistoryTable: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { data, loading } = useQuery(bodyMeasurementsHistoryTableQuery, {
    variables: { id: clientId ?? "" },
  });
  const [deleteBodyMeasurements] = useMutation(deleteBodyMeasurementsMutation);
  const bodyMeasurements = data?.client?.bodyMeasurementsByDay?.nodes ?? [];
  const settings = data?.client?.setting;
  const alert = useAlert();
  const snackbar = useGlobalSnackbar();
  const deleteDialog = useDialogState();
  const [bmDetailsModal, setbmDetailsModal] = useState<boolean>(false);
  const [addBmModal, setAddBmModal] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentBm, setCurrentBmWeek] =
    useState<BodyMeasurementDetailsModalFragment | undefined>(undefined);
  const targetWeight = data?.client?.journalGoal?.targetWeightKg;
  const latestHeightCm = data?.client?.latestHeightCm?.value;
  const gender = data?.client?.profile?.gender;
  const latestWeightKg = data?.client?.latestWeightKg?.value;

  const idealBodyWeight = round(
    getDevineIdealBodyWeight(latestHeightCm, gender) ?? 0,
    2
  );

  const getField = (fieldName: string) => {
    return find(settings?.bodyMeasurementFields, {
      fieldName: snakeCase(fieldName),
    });
  };

  const handleOpenBmDetailsModal = (day: string) => {
    if (isEmpty(bodyMeasurements)) return;
    const bm = bodyMeasurements.find((node) => node?.day === day);
    if (isEmpty(bm)) return;
    setbmDetailsModal(true);
    setCurrentBmWeek(bm);
  };

  const handleCloseBmDetailsModal = () => {
    setbmDetailsModal(false);
    setCurrentBmWeek(undefined);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setAddBmModal(true);
    setbmDetailsModal(false);
  };

  const handleOpenAddBmModal = () => {
    setAddBmModal(true);
  };

  const handleCloseAddBmModal = () => {
    setAddBmModal(false);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (currentBm == null) return;
    try {
      const result = await deleteBodyMeasurements({
        variables: {
          input: { clientId, date: currentBm.bodyMeasurements.date },
        },
        refetchQueries: [
          {
            query: bodyMeasurementsHistoryTableQuery,
            variables: { id: clientId ?? "" },
          },
        ],
      });
      if (result.data) alert.open("Body measurements deleted successfully.");
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "Something went wrong while deleting body measurements. Please try again later."
        )
      );
    }
  };

  const { classes, cx } = useStyles();

  const _rows = () => {
    return bodyMeasurements.map((node) => {
      if (!node) return null;
      const date = node.day ? format(new Date(node.day), "dd MMM, yyyy") : "-";
      const time = node.bodyMeasurements.createdAt
        ? format(new Date(node.bodyMeasurements.createdAt), "hh:mm bb")
        : "-";
      return (
        <StyledTableRow key={node.day}>
          <StyledTableCell
            className={classes.stickyLeftCell}
            component="th"
            scope="row"
          >
            <Typography variant="body2" className={classes.cellLabel}>
              {date}
            </Typography>
            <span className={classes.textSecondary}>{time}</span>
          </StyledTableCell>
          {Object.entries(
            omit(node.bodyMeasurements, ["__typename", "images"])
          ).map(([key, value]) => {
            const field = getField(key);
            if (field === undefined) return;
            const isDefault = FIELDS[key] === "defaultField";
            if (snakeCase(key) === field.fieldName) {
              if (isDefault) {
                return (
                  <StyledTableCell align="center" key={key}>
                    <Typography variant="body2" className={classes.cellLabel}>
                      {value == null ? "-" : value}
                    </Typography>
                  </StyledTableCell>
                );
              } else {
                if (field.visible) {
                  return (
                    <StyledTableCell align="center" key={key}>
                      <Typography variant="body2" className={classes.cellLabel}>
                        {value == null ? "-" : value}
                      </Typography>
                    </StyledTableCell>
                  );
                }
              }
            }
          })}
          <StyledTableCell
            align="center"
            className={cx(classes.moreCell, classes.stickyRightCell)}
          >
            <OptionMenu
              onOpen={() => handleOpenBmDetailsModal(node.day)}
              onDelete={() => {
                setCurrentBmWeek(node);
                deleteDialog.show();
              }}
            />
          </StyledTableCell>
        </StyledTableRow>
      );
    });
  };

  const _tableHeads = () => {
    if (bodyMeasurements == null) return;
    const node = bodyMeasurements[0] ?? null;
    if (node == null) return;

    return Object.entries(
      omit(node.bodyMeasurements, ["__typename", "images"])
    ).map(([key], index) => {
      const field = getField(key);
      if (field === undefined) return;
      const isDefault = FIELDS[key] === "defaultField";
      if (snakeCase(key) === field.fieldName) {
        if (isDefault) {
          return (
            <StyledTableCellHead key={index}>
              {field.fieldLabel}
            </StyledTableCellHead>
          );
        } else {
          if (field.visible) {
            return (
              <StyledTableCellHead key={index}>
                {field.fieldLabel}
              </StyledTableCellHead>
            );
          }
        }
      }
    });
  };

  if (loading) return null;

  return (
    <Box className={classes.root}>
      <Box
        className={classes.header}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography className={classes.headerText}>
          Body Measurement History
        </Typography>
        <Box display="flex" alignItems="center">
          <Box
            className={classes.summaryItems}
            display="flex"
            alignItems="center"
          >
            <AccordionSummaryItem
              title="Current Height"
              text={`${data?.client?.latestHeightCm?.value ?? 0} cm`}
              icon="height"
            />
            <AccordionSummaryItem
              title="Goal Weight"
              text={`${truncate(kilogramsToPounds(targetWeight ?? 0), 1)} lbs`}
              secondaryText={`(${targetWeight} kg)`}
              icon="goal-weight"
            />
            <AccordionSummaryItem
              title="Ideal Weight"
              text={`${idealBodyWeight} kg`}
              secondaryText={`(${round(
                kilogramsToPounds(idealBodyWeight),
                2
              )} lbs)`}
              icon="ideal-weight"
            />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={2}
          >
            <StyledButton
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddBmModal}
            >
              Add
            </StyledButton>
          </Box>
        </Box>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <StyledTableHeadRow>
              {!isEmpty(bodyMeasurements) && (
                <StyledTableCellHead
                  className={cx(classes.dateLabel, classes.stickyLeftCell)}
                  height="5%"
                >
                  Date
                </StyledTableCellHead>
              )}
              {_tableHeads()}
              <StyledTableCellHead
                className={cx(
                  classes.moreCell,
                  classes.moreLabel,
                  classes.stickyRightCell
                )}
              ></StyledTableCellHead>
            </StyledTableHeadRow>
          </TableHead>
          <TableBody>{_rows()}</TableBody>
        </Table>
      </TableContainer>
      {isEmpty(bodyMeasurements) && (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="h6" color="textSecondary">
            This client does not have any entries yet
          </Typography>
        </Box>
      )}
      <BodyMeasurementDetailsModal
        targetWeight={data?.client?.journalGoal?.targetWeightKg}
        settings={settings}
        bm={currentBm}
        open={bmDetailsModal}
        onClose={handleCloseBmDetailsModal}
        onEdit={handleEdit}
        onDelete={deleteDialog.show}
        latestHeightCm={latestHeightCm}
        latestWeightKg={latestWeightKg}
        gender={gender}
      />
      <AddBodyMeasurementModal
        settings={settings}
        visibility={addBmModal}
        isEditing={isEditing}
        onClose={handleCloseAddBmModal}
      />
      <DeleteModal
        visible={deleteDialog.shown}
        title=""
        message="Are you sure you want to delete body measurements for this date?"
        onDelete={() => {
          handleDelete();
          handleCloseBmDetailsModal();
        }}
        onClose={deleteDialog.hide}
      />
    </Box>
  );
};

export default BodyMeasurementHistoryTable;
